import Axios from "axios";
import {
  AnsweringClient,
  ApiPurchasePayment,
  ApiPurchaseUser,
  CardBrand,
  IInstrument,
  UpdateUserProfilePayload,
  UserProfile,
} from "../clients/answeringClient";
import { environment } from "../utils/environment";
import { State } from "./data-types";

export interface CEP {
  cep: string;
  state: State;
  city: string;
  street: string;
  neighborhood: string;
  service: string;
}

export enum PaymentMethod {
  CREDIT_CARD = "CREDIT_CARD",
  PIX = "PIX",
  BANK_SLIP = "BANK_SLIP"
}

export type CheckoutPurchaseItem = Omit<IInstrument, "description"> & {
  description?: string;
  priceChanged?: boolean;
  priceSnapshotCents?: number | null;
  unitPriceCents: number;
  discountedUnitPriceCents: number;
  subtotalCents: number;
  discountedSubtotalCents: number;
  totalDiscountCents: number;
  appliedDiscountPct: number | null;
};

export interface IFullPaymentData {
  purchaseData: CheckoutPurchaseItem[];
  selectedPaymentMethod?: PaymentMethod | null;
  user: {
    identification: string;
    identificationType?: "CPF" | "CNPJ";
    cnpj?: string;
    birthDate: {
      day: string;
      month: string;
      year: string;
    };
    cellphone: string;
  };
  address: {
    cep: string;
    isProvidedByCep: {
      state: boolean;
      city: boolean;
      neighborhood: boolean;
      street: boolean;
    };

    state: State;
    city: string;
    neighborhood: string;
    street: string;
    number: string;
    completion: string;
  };
  creditCard: {
    cvc: string;
    number: string;
    expiry: string;
    name: string;
    issuer: string;
    installments?: number;
  };
}

export interface PaymentResponse {
  orderNumber?: string;
  paymentStatusId?: number;
  paymentStatusName?: string;
  paymentMethodId?: number;
  paymentMethodName?: string;
  installments?: number;
  expired?: boolean;
  dueDate?: Date;
  paymentUrl?: string;
  paymentQrCode?: string;
  paymentPixCopyPaste?: string;
}

export interface InstallmentOption {
  n: number;
  no_interest: boolean;
  value_split_cents?: number;
  value_total_cents?: number;
  gateway_rate_percent?: number;
}

export interface InstallmentsResponse {
  brand: string;
  amount_cents: number;
  max_no_interest: number;
  max_total: number;
  min_installment_value_cents: number;
  max_allowed_installments_for_amount: number;
  options: InstallmentOption[];
  simulated: boolean;
  simulation?: {
    payment_method_id: number;
    payment_method_name: string;
    splittings: Array<{
      split: number;
      value_split: string;
      value_transaction: string;
      split_rate: number | string;
    }>;
  };
}

export interface PaymentSummaryResponse {
  method: PaymentMethod;
  brand?: string;
  amount_cents: number;
  policy: {
    max_no_interest: number;
    max_total: number;
    min_installment_value_cents: number;
    allowed_card_brands: string[];
    max_allowed_installments_for_amount?: number;
  };
  method_adjustment_cents: number;
  estimated_final_amount_cents: number;
  interest_source?: string;
  notes?: string;
  validation?: {
    installments_no_interest?: boolean;
  };
}

export interface PaymentStatusResponse {
  orderNumber: string;
  paymentStatusId: number;
  paymentStatusName: string;
  paymentMethodId: number;
  paymentMethodName: string;
  installments: number;
  paymentUrl?: string;
  pixQrcodePath?: string;
  pixQrcodeOriginalPath?: string;
  paymentDueAt?: string;
  expired: boolean;
}

export interface CheckoutPaymentControllerDelegate {
  didGetInstruments(instruments: IInstrument[]): void;
  didGetCepData(data: CEP): void;
  didCreatePurchase(data: PaymentResponse): void;
  applicationShouldLogout(): void;
  errorCreatingPurchase(options: {
    isValidation: boolean;
    message?: string;
  }): void;
  genericError(message?: string): void;
  cepDataError(): void;
}

export class CheckoutPaymentController {
  constructor(
    private delegate: CheckoutPaymentControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) { }

  private readonly paymentsBasePath = "api/v1/admin/payments";

  public async getCepData(accessToken: string, cep: string) {
    try {
      const response = await this.answeringClient.getAddressData(
        accessToken,
        cep
      );
      this.delegate.didGetCepData(response.data);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.getCepData(newTokens.accessToken, cep);
        return;
      }
      this.delegate.cepDataError();
    }
  }

  public async createPuchase(
    accessToken: string,
    fingerprint: string,
    paymentData: IFullPaymentData,
    addressId?: number,
    cartId?: number,
    couponCode?: string
  ) {
    if (addressId === undefined || addressId === null) {
      this.delegate.errorCreatingPurchase({
        isValidation: true,
        message: "Selecione um endereço válido para concluir a compra.",
      });
      return;
    }

    if (cartId === undefined || cartId === null) {
      this.delegate.errorCreatingPurchase({
        isValidation: true,
        message:
          "Não foi possível identificar o carrinho. Atualize a página e tente novamente.",
      });
      return;
    }

    const user = {
      identification: paymentData.user.identification,
      cnpj: paymentData.user.cnpj || undefined,
      birthDate: {
        day: Number.parseInt(paymentData.user.birthDate.day),
        month: Number.parseInt(paymentData.user.birthDate.month),
        year: Number.parseInt(paymentData.user.birthDate.year),
      },
      contact: {
        type: "MOBILE" as "MOBILE",
        phoneNumber: paymentData.user.cellphone,
      },
    } as ApiPurchaseUser;

    const payment = {
      fingerprint,
      type: paymentData.selectedPaymentMethod!,
    } as ApiPurchasePayment;

    if (paymentData.selectedPaymentMethod === PaymentMethod.CREDIT_CARD) {
      payment.creditCardInfo = {
        // Typed cast (not `as any`): the UI normalizes the issuer to the brand
        // enum (didIdentifyCreditCard) and gates the purchase on a known brand,
        // so this is always a valid CardBrand at runtime.
        issuer: paymentData.creditCard.issuer.toUpperCase() as CardBrand,
        cardCvv: paymentData.creditCard.cvc,
        cardExpirationMonth: paymentData.creditCard.expiry.substring(0, 2),
        cardExpirationYear: paymentData.creditCard.expiry.substring(2),
        cardName: paymentData.creditCard.name,
        cardNumber: paymentData.creditCard.number,
        installments: paymentData.creditCard.installments ?? 1,
      };
    }

    try {
      const response = await this.answeringClient.createPurchase(
        accessToken,
        cartId,
        user,
        payment,
        addressId,
        couponCode
      );

      const raw = response.data as PaymentResponse & {
        dueDate?: string;
        paymentDueAt?: string;
      };

      const responseData: PaymentResponse = {
        ...raw,
      };

      if (raw.dueDate) {
        responseData.dueDate = new Date(raw.dueDate);
      } else if (raw.paymentDueAt) {
        responseData.dueDate = new Date(raw.paymentDueAt);
      }

      this.delegate.didCreatePurchase(responseData);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.createPuchase(
          newTokens.accessToken,
          fingerprint,
          paymentData,
          addressId,
          cartId,
          couponCode
        );
        return;
      }
      const message =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        (Array.isArray(error?.response?.data?.errors)
          ? error.response.data.errors[0]?.message
          : undefined) ??
        error?.message ??
        "Não foi possível concluir o pagamento.";

      this.delegate.errorCreatingPurchase({
        isValidation: error?.response?.status === 422,
        message,
      });
    }
  }

  public async getUserProfile(accessToken: string): Promise<UserProfile | undefined> {
    try {
      const response = await this.answeringClient.getUserProfile(accessToken);
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return undefined;
        }
        return this.getUserProfile(newTokens.accessToken);
      }
      if (error?.response?.status === 404) {
        return undefined;
      }
      throw error;
    }
  }

  public async updateUserProfile(
    accessToken: string,
    payload: UpdateUserProfilePayload
  ): Promise<UserProfile | undefined> {
    try {
      const response = await this.answeringClient.updateUserProfile(accessToken, payload);
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          throw error;
        }
        return this.updateUserProfile(newTokens.accessToken, payload);
      }
      throw error;
    }
  }

  public async getManagedInstruments(
    accessToken: string,
    instrumentIds?: number[]
  ) {
    try {
      const response = await this.answeringClient.getAllManagedInstruments(
        accessToken
      );
      let data = response.data.map((instrument) => ({
        title: instrument.title,
        description: instrument.description,
        priceInBrlCents: instrument.priceInBrlCents,
        quantity: instrument.quantity,
        id: instrument.id,
      })) as IInstrument[];

      if (instrumentIds?.length) {
        const allowedIds = new Set(instrumentIds);
        data = data.filter((instrument) => allowedIds.has(instrument.id));
      }
      this.delegate.didGetInstruments(data);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.getManagedInstruments(newTokens.accessToken, instrumentIds);
        return;
      }
      this.delegate.genericError(
        "Não foi possível carregar os detalhes dos instrumentos."
      );
    }
  }

  public async getInstallmentOptions(
    accessToken: string,
    params: {
      amountCents: number;
      brand: string;
      simulate?: boolean;
    }
  ): Promise<InstallmentsResponse> {
    const query: Record<string, unknown> = {
      amount_cents: params.amountCents,
      brand: params.brand.toUpperCase(),
    };

    if (params.simulate !== undefined) {
      query.simulate = params.simulate;
    }

    try {
      const response = await Axios.get<InstallmentsResponse>(
        `${environment.apiBaseUrl}${this.paymentsBasePath}/installments`,
        {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
          params: query,
        }
      );

      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          throw error;
        }
        return this.getInstallmentOptions(newTokens.accessToken, params);
      }
      throw error;
    }
  }

  public async getPaymentSummary(
    accessToken: string,
    params: {
      method: PaymentMethod;
      amountCents: number;
      brand?: string;
      installments?: number;
    }
  ): Promise<PaymentSummaryResponse> {
    const query: Record<string, unknown> = {
      method: params.method,
      amount_cents: params.amountCents,
    };

    if (params.brand) {
      query.brand = params.brand.toUpperCase();
    }

    if (params.installments !== undefined) {
      query.installments = params.installments;
    }

    try {
      const response = await Axios.get<PaymentSummaryResponse>(
        `${environment.apiBaseUrl}${this.paymentsBasePath}/summary`,
        {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
          params: query,
        }
      );

      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          throw error;
        }
        return this.getPaymentSummary(newTokens.accessToken, params);
      }
      throw error;
    }
  }

  public async getPaymentStatus(
    accessToken: string,
    orderNumber: string
  ): Promise<PaymentStatusResponse> {
    try {
      const response = await Axios.get<PaymentStatusResponse>(
        `${environment.apiBaseUrl}${this.paymentsBasePath}/${orderNumber}/status`,
        {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          throw error;
        }
        return this.getPaymentStatus(newTokens.accessToken, orderNumber);
      }
      throw error;
    }
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }
}
